/**
 * An address box that suggests, but never insists.
 *
 * Two rules, both learned from what a dispatcher is actually doing while they use it.
 *
 * **The caller's own addresses come first.** Someone who rings every Tuesday from the
 * same doorway wants that doorway, not the closest thing the map found to what they said.
 *
 * **Anything typed is allowed.** A taxi firm meets new addresses all day — a building
 * finished last month, a village the gazetteer has never heard of, "the blue gate behind
 * the school". Refusing an address because it is not already in a list would make the
 * common case the broken one. What is typed is geocoded when the order is taken, and
 * saved to the caller afterwards, so the second time it is a suggestion.
 *
 * **The map search stays in town unless told otherwise** (#382). A firm that has drawn its
 * town on Settings → Address search gets a switch above the addresses — town, nearby, wider,
 * automatic — held by the form, and the search keeps to it. Nothing found at a level says so
 * and offers the next level in one click. Automatic widens by itself, a level at a time, and
 * marks every address it found outside town with the level, so the dispatcher still sees that
 * it is out of town. Alt and the position's number move the switch without leaving the box.
 *
 * **The firm's own addresses come first, and say so** (#420). The server lists them above the
 * map's results; the box marks each — one waiting for the administrator, one a driver has
 * reported, an entrance typed that nobody has placed — and reads the firm's note for it under
 * the address once it is chosen. A building's entrances are offered one of two ways, the
 * firm's choice unless the dispatcher made their own: **in the list**, each door a row, or
 * **after the address**, the building one row and its letters under the box once it is
 * picked — a letter key takes the door, Enter keeps the building, and a letter nobody has
 * placed goes in on the building's point for the driver to be asked about at the door.
 *
 * **A street's numbers are offered the dispatcher's way.** **In the list**, the default: a
 * street the search found alone comes with its first numbers under it, in order — 1, 2, 3, 4,
 * 4А. **After the street**: picking the street writes its name in the box and lists the street
 * and every number on it; typing the number narrows the list — `Росица 1` keeps 1, 10, 10А —
 * without asking the server again, and a number the map does not have is searched as typed.
 */

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';

import { canonicalEntrance, foldEntrances, namesADoor, writeEntrance, type Folded } from '../places/entrances';
import type { NumberWay } from '../shell/numberWay';
import { CONTROL, cx, Icon, Pill } from '../ui';
import { customersApi, type AddressSuggestion, type DrawnLevels, type SearchMode } from './api';
import {
  numbersOf,
  onStreet,
  placesOf,
  scopeName,
  scopePositions,
  searchMap,
  type MapSearch,
  type Place,
  type StreetListing,
} from './addresses';

/** How the box offers a building's entrances: each a row, or as letters after the address. */
export type EntranceWay = 'list' | 'after';

export function AddressField({
  id,
  label,
  text,
  onText,
  onChosen,
  chosen: resolved = null,
  customerId,
  saved,
  required = false,
  className,
  levels = null,
  scope = 'town',
  onScope,
  ref,
  entrances = 'list',
  numbers = 'list',
  labelAction,
}: {
  id: string;
  label: string;
  text: string;
  onText: (value: string) => void;
  onChosen: (place: Place | null) => void;

  /**
   * The place currently behind the text, when one has been picked.
   *
   * Only to draw with. An address with coordinates and one without look identical in a box,
   * and the difference is whether the car is being sent somewhere or a geocoder is about to
   * guess — so a resolved field says so, in the brand, with a tick.
   */
  chosen?: Place | null;
  customerId: string | null;
  /** The caller's addresses, offered before anything is typed. */
  saved: AddressSuggestion[];
  required?: boolean;
  className?: string;
  /** Which levels the firm drew; null when none are, and then there is no switch. */
  levels?: DrawnLevels | null;
  /** How far the map search reaches. Held by the form, which draws the switch, so a new order starts in town. */
  scope?: SearchMode;
  onScope?: (scope: SearchMode) => void;
  /** The text box, for a form that moves the cursor into it. */
  ref?: Ref<HTMLInputElement>;
  /** How a building's entrances are offered; the firm's, unless the dispatcher chose. */
  entrances?: EntranceWay;
  /** How a street's numbers are offered: the dispatcher's own way. */
  numbers?: NumberWay;
  /** Something to do with the address, beside its label — the pickup's Заключи…. */
  labelAction?: ReactNode;
}) {
  const { t } = useTranslation();

  const [suggestions, setSuggestions] = useState<Place[]>([]);

  /** The position a search found nothing at, so the list can say so. */
  const [emptyIn, setEmptyIn] = useState<SearchMode | null>(null);

  /** The last search went without our map, which was not answering: the list says so. */
  const [mapMissing, setMapMissing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => input.current as HTMLInputElement);

  const positions = scopePositions(levels);
  const searchedIn = positions.length > 0 ? scope : undefined;

  /** The next level out, for the button under an empty list. Never Automatic: it reaches no further. */
  const wider =
    searchedIn === undefined || searchedIn === 'auto'
      ? undefined
      : positions.filter((position) => position !== 'auto')[
          positions.filter((position) => position !== 'auto').indexOf(searchedIn) + 1
        ];
  const [highlighted, setHighlighted] = useState(0);
  const [open, setOpen] = useState(false);

  /** A building just picked whose letters wait under the box, in the *after the address* way. */
  const [doorsOf, setDoorsOf] = useState<{ building: Place; doors: Place[] } | null>(null);

  /** A street just picked whose numbers are listed, in the *after the street* way. */
  const [street, setStreet] = useState<StreetListing | null>(null);

  /** Its numbers as far as the box has typed them; null once the text has left the street. */
  const onTheStreet = street === null ? null : onStreet(text, street);

  // The list as it is drawn: a picked street's numbers while its number is being typed; or a
  // building's doors folded behind it when the firm or the dispatcher wants them after the
  // address — unless what was typed already names a door, which is then the first row rather
  // than a letter to press.
  const rows: Folded<Place>[] =
    onTheStreet !== null
      ? onTheStreet.map((place) => ({ kind: 'one', place }))
      : entrances === 'after' && !namesADoor(text)
        ? foldEntrances(suggestions)
        : suggestions.map((place) => ({ kind: 'one', place }));

  // Held in a ref so the lookup effect does not restart every time the parent re-renders
  // with a new closure, which would cancel the debounce on every keystroke of context.
  const chosen = useRef(onChosen);

  useEffect(() => {
    chosen.current = onChosen;
  });

  useEffect(() => {
    const query = text.trim();

    // A picked street's number being typed is narrowed from the numbers already here.
    if (street !== null && onStreet(text, street) !== null) return;

    const timer = setTimeout(() => {
      void (async () => {
        if (query.length === 0) {
          // Nothing typed yet: their own history is the best guess available.
          setSuggestions(placesOf(saved));
          setEmptyIn(null);
          setMapMissing(false);
          setHighlighted(0);
          return;
        }

        const own = customerId !== null
          ? await customersApi.searchAddresses(customerId, query).catch(() => [])
          : [];

        const found = await searchMap(query, searchedIn, numbers === 'list')
          .catch((): MapSearch => ({ places: [], mapUnavailable: false }));

        // Nothing found for want of the map is not nothing there, and no wider level will help.
        setEmptyIn(
          own.length === 0 && found.places.length === 0 && searchedIn !== undefined && !found.mapUnavailable
            ? searchedIn
            : null,
        );
        setMapMissing(found.mapUnavailable);

        setSuggestions([...placesOf(own), ...found.places]);
        setHighlighted(0);
      })();
    }, 250);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, customerId, saved, searchedIn, numbers, street]);

  // A long street's numbers scroll: the row the arrows reach is kept in view.
  useEffect(() => {
    if (open) document.getElementById(`${id}-${highlighted}`)?.scrollIntoView({ block: 'nearest' });
  }, [id, open, highlighted]);

  const take = useCallback((place: Place) => {
    onText(place.address);
    chosen.current(place);
    setOpen(false);
    setDoorsOf(null);
    setStreet(null);
  }, [onText]);

  /**
   * A street picked in the *after the street* way: its name in the box for the number to be
   * typed after it, and the street and every number on it in the list. A street the map has
   * no numbers for is taken as it is, as it always was.
   */
  async function openStreet(place: Place) {
    const listing = await numbersOf(place, searchedIn).catch(() => null);

    if (listing === null || listing.numbers.length === 0) {
      take(place);
      return;
    }

    setStreet(listing);
    onText(`${listing.name} `);
    chosen.current(null);
    setDoorsOf(null);
    setHighlighted(0);
    setOpen(true);
    input.current?.focus();
  }

  /** A row picked: a building with its doors folded keeps the building and offers the letters. */
  function pick(row: Folded<Place>) {
    if (numbers === 'after' && row.kind === 'one' && row.place.street != null && row.place !== street?.street) {
      void openStreet(row.place);
      return;
    }

    // The building made from its first door is not that door: it carries no place of its own.
    take(row.kind === 'building' ? { ...row.place, placeId: null } : row.place);

    if (row.kind === 'building') {
      setDoorsOf({ building: row.place, doors: row.doors });
      input.current?.focus();
    }
  }

  /**
   * A letter pressed with a building's doors under the box: that door, or — for a letter
   * nobody has placed — the building's point with the entrance written on, which the
   * driver's phone will offer to remember at the door.
   */
  function door(letter: string) {
    if (doorsOf === null) return;

    const placed = doorsOf.doors.find((candidate) => candidate.firm?.entrance === letter);

    if (placed !== undefined) {
      take(placed);
      return;
    }

    take({
      ...doorsOf.building,
      address: writeEntrance(doorsOf.building.address, letter),
      placeId: null,
      firm: {
        id: null,
        building: doorsOf.building.address,
        entrance: letter,
        waiting: false,
        reported: false,
        name: null,
        note: null,
        door: null,
      },
    });
  }

  /** Moves the switch and puts the dispatcher back in the box, list open, to keep typing. */
  function widenTo(next: SearchMode) {
    onScope?.(next);
    setOpen(true);
    input.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Alt and a digit, by the key's place rather than its character, which a keyboard
    // layout may change under Alt.
    if (event.altKey && positions.length > 0 && /^Digit[1-9]$/.test(event.code)) {
      const next = positions[Number(event.code.slice(5)) - 1];

      event.preventDefault();

      if (next !== undefined) widenTo(next);

      return;
    }

    // A building's letters under the box: a letter key is a door, Enter keeps the building
    // — and does not take the order, which would go before the dispatcher chose — and
    // Escape puts the letters away.
    if (doorsOf !== null) {
      const letter =
        event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey
          ? canonicalEntrance(event.key)
          : null;

      if (letter !== null) {
        event.preventDefault();
        door(letter);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setDoorsOf(null);
        return;
      }
    }

    if (!open || rows.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((i) => (i + 1) % rows.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((i) => (i - 1 + rows.length) % rows.length);
    } else if (event.key === 'Enter' && rows[highlighted]) {
      // Enter picks the highlighted address rather than submitting the form, which would
      // otherwise send an order with whatever half-typed text was in the box.
      event.preventDefault();
      pick(rows[highlighted]);
    } else if (event.key === 'Escape') {
      // Consumed here, so the first Escape closes the list and only a second one reaches
      // the form, which clears everything — a dispatcher dismissing a suggestion must not
      // lose the number they have typed.
      event.stopPropagation();
      setOpen(false);
    }
  }

  const showing = open && rows.length > 0;
  const nothing = open && suggestions.length === 0 && emptyIn !== null && text.trim().length > 0;

  /** Said above the list, or alone where the list would be, while the map is not answering. */
  const missing = open && mapMissing && onTheStreet === null && text.trim().length > 0;
  const mapNote = (
    <span className="flex items-start gap-1.5 text-secondary text-ink-2">
      <Icon name="info" small className="mt-0.5 shrink-0 text-warn" />
      {t('order.mapMissing')}
    </span>
  );

  const settled = resolved !== null && resolved.address === text;

  return (
    <div
      className={cx('flex flex-col relative', className)}
      // Closed when focus leaves the field, not the box: Tab from the box goes to the
      // "search nearby" button under it, and Enter presses it (#382). On a delay, because a
      // click on the list blurs the box first and an immediate close would remove the option
      // before the click landed on it.
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;

        setTimeout(() => setOpen(false), 120);
      }}
    >
      <span className="flex items-center justify-between gap-2 mb-1">
        <label htmlFor={`${id}-input`} className="text-secondary font-medium text-ink-2">
          {label}
        </label>
        {labelAction}
      </span>
      <input
        ref={input}
        id={`${id}-input`}
        className={cx(CONTROL, 'placeholder:text-ink-3 pr-8', settled ? 'border-brand' : '')}
        value={text}
        onChange={(event) => {
          onText(event.target.value);
          // Typing invalidates whatever was picked: the text and the coordinates must
          // never disagree, or the car goes to the previous address under a new name.
          chosen.current(null);
          setDoorsOf(null);
          setOpen(true);

          // A street's numbers narrowing as the number is typed: the first that still fits.
          if (street !== null) setHighlighted(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={showing}
        aria-controls={`${id}-options`}
        aria-activedescendant={showing ? `${id}-${highlighted}` : undefined}
        required={required}
      />

      {/* The tick is the whole point of the border: it says this address has coordinates and
          the car is being sent to a place rather than to a string. */}
      {settled && (
        <Icon name="check" small className="absolute right-2.5 bottom-2 text-ok pointer-events-none" />
      )}

      {showing && (
        <ul
          className="absolute top-full left-0 right-0 z-20 mt-1 max-h-80 overflow-y-auto m-0 p-1 list-none bg-surface border border-rule rounded-card shadow-float"
          id={`${id}-options`}
          role="listbox"
        >
          {missing && (
            <li role="presentation" className="px-2 py-1.5 border-b border-rule">
              {mapNote}
            </li>
          )}
          {rows.map((row, index) => {
            const place = row.place;

            return (
              <li
                key={`${place.address}-${index}`}
                id={`${id}-${index}`}
                role="option"
                aria-selected={index === highlighted}
                className={cx(
                  'flex items-center justify-between gap-2 px-2 py-1.5 rounded-control cursor-pointer',
                  index === highlighted ? 'bg-brand-soft' : '',
                )}
                // Mouse down rather than click: click fires after blur, by which point the
                // list has already closed.
                onMouseDown={() => pick(row)}
              >
                {/* Wrapped, not cut: a stop with its direction, or a shop with its Bulgarian
                    and its Latin name, is longer than the field, and the end of the line is
                    what tells it from the row above. */}
                <span className="min-w-0 break-words">
                  {/* The firm's own, by the pin it was placed with — the one thing in the
                      list nobody's map supplied. */}
                  {place.firm?.id != null && (
                    <Icon name="pin" small className="inline-block mr-1 -mt-0.5 text-brand" />
                  )}
                  {place.address}
                </span>
                <span className="shrink-0 flex items-center gap-1.5 text-secondary text-ink-3">
                  {row.kind === 'building' && (
                    <Pill tone="brand" soft>
                      {t('order.entrances.count', { count: row.doors.length })}
                    </Pill>
                  )}
                  {/* The picked street itself, above its numbers: how many there are to choose from. */}
                  {street !== null && place === street.street && (
                    <Pill tone="brand" soft>
                      {t('order.numbers.count', { count: street.numbers.length })}
                    </Pill>
                  )}
                  {place.firm?.name != null && !place.address.startsWith(place.firm.name) && place.firm.name}
                  {place.firm?.waiting === true && (
                    <Pill tone="warn" soft title={t('order.entrances.waitingAbout')}>
                      {t('order.entrances.waiting')}
                    </Pill>
                  )}
                  {place.firm?.reported === true && (
                    <Pill tone="danger" soft title={t('order.entrances.reportedAbout')}>
                      {t('order.entrances.reported')}
                    </Pill>
                  )}
                  {place.firm != null && place.firm.id == null && (
                    <Pill tone="neutral" soft title={t('order.entrances.unplacedAbout')}>
                      {t('order.entrances.unplaced')}
                    </Pill>
                  )}
                  {place.label != null && place.label}
                  {place.useCount != null && ` ${place.useCount}×`}
                  {/* Where the automatic search had to go to find it: quiet, but there. */}
                  {place.foundIn !== undefined && scopeName(t, place.foundIn, levels)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* A building's doors, after the address: the letters under the box, pressed as keys or
          clicked. Not a list that opens over the form — the letters are the whole choice. */}
      {doorsOf !== null && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5" role="group" aria-label={t('order.entrances.label')}>
          <span className="text-secondary text-ink-2">{t('order.entrances.label')}</span>
          {doorsOf.doors.map((candidate) => (
            <button
              key={candidate.firm?.entrance ?? candidate.address}
              type="button"
              tabIndex={-1}
              className="h-7 min-w-7 px-2 rounded-control border border-rule bg-surface text-[13px] font-semibold text-ink hover:bg-surface-2"
              title={candidate.address}
              onMouseDown={(event) => {
                event.preventDefault();
                take(candidate);
              }}
            >
              {candidate.firm?.entrance}
            </button>
          ))}
          <span className="text-secondary text-ink-3">{t('order.entrances.keys')}</span>
        </div>
      )}

      {/* What the firm wrote on its place, under the address once it is chosen (#433): the
          dispatcher reads it to the caller if it matters, and the driver gets it on the job. */}
      {settled && resolved?.firm?.note != null && (
        <span className="mt-1 flex items-start gap-1.5 text-secondary text-ink-2">
          <Icon name="info" small className="mt-0.5 shrink-0" />
          {resolved.firm.note}
        </span>
      )}

      {/* The map not answering and nothing of the firm's or the caller's found either: said,
          so an empty list does not read as an address that does not exist. */}
      {missing && !showing && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 p-2 bg-surface border border-rule rounded-card shadow-float">
          {mapNote}
        </div>
      )}

      {/* Nothing found: said, and the next level offered. The manual positions never widen
          for the dispatcher — a street not in town is something to notice; Automatic does. */}
      {nothing && emptyIn !== null && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 flex items-center justify-between gap-2 p-2 bg-surface border border-rule rounded-card shadow-float">
          <span className="text-secondary text-ink-2">
            {emptyIn === 'auto'
              ? t('order.scope.nothing')
              : t('order.scope.nothingIn', { level: scopeName(t, emptyIn, levels) })}
          </span>
          {wider !== undefined && (
            <button
              type="button"
              className="shrink-0 h-7 px-2.5 rounded-control border border-rule bg-surface text-[13px] text-ink hover:bg-surface-2"
              title={t('order.scope.shortcut', { key: positions.indexOf(wider) + 1 })}
              // A click, so Enter presses it once Tab has brought the focus here; the field
              // stays open while the focus is on it.
              onClick={() => widenTo(wider)}
            >
              {t('order.scope.widen', { level: scopeName(t, wider, levels) })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
